import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  acceptMeet,
  declineMeet,
  forwardMeet,
  incomingMeetsList,
} from "../../../../../library/store/actions/creators/uknowCreators"
import Table, {
  recordsPerPageOptions,
} from "../../../../../components/v2/table/table"
import { TablePagination, Tooltip } from "@material-ui/core"
import styled from "@emotion/styled"
import { colors } from "../../../../../library/constants/styles/colors"
import * as qs from "query-string"
import { Route, useHistory, useLocation, useRouteMatch } from "react-router-dom"
import MeetModalsProvider, {
  useMeetModals,
} from "../../../../../hooks/entities/useMeetsModals"
import UpdateIcon from "@material-ui/icons/UpdateOutlined"
import {
  KNOWMAP_INCOMING_KNOWMEET,
  KNOWMAP_INCOMING_MEET,
  KNOWMAP_INCOMING_MEETS,
} from "../../../../../library/constants/routes"
import MeetDialog from "./MeetDialog"
import { useAlert } from "../../../../../hooks/useAlert"
import CheckOutlined from "@material-ui/icons/CheckOutlined"
import CloseOutlined from "@material-ui/icons/CloseOutlined"
import composeQuery from "../../../../../library/utils/composeQuery"
import { SearchOutlined } from "@material-ui/icons"
import Stub from "../../../../../components/stub"

const columns = [
  { id: 1, label: "Name" },
  { id: 2, label: "Target/Set Date" },
  { id: 3, label: "Creator" },
  { id: 4, label: "Location" },
  { id: 5, label: "Instructions" },
]

const Container = styled.div`
  padding: 15px 10px;
  box-shadow: 0 0 2px 0 ${colors.black12};
  background-color: ${colors.white};
  border-radius: 2px;
`

const MeetsTable = ({ getRecords }) => {
  const history = useHistory()
  const location = useLocation()
  const { path } = useRouteMatch()

  const { successAlert, errorAlert } = useAlert()

  const dispatch = useDispatch()
  const { incomingMeets } = useSelector(({ uknow }) => uknow)

  const [name, setName] = useState("")
  const [page, setPage] = useState(0)
  const [recordsPerPage, setRecordsPerPage] = useState(recordsPerPageOptions[0])

  const [dataReceived, setDataReceived] = useState(false)
  const [changingPage, setChangingPage] = useState(false)

  const fetchData = async (offset = 0, shouldPageChange = false) => {
    const { q } = qs.parse(location.search)
    if (shouldPageChange) setChangingPage(true)

    await getRecords({
      name: q || undefined,
      offset,
      limit: recordsPerPage,
    })

    setDataReceived(true)
    if (shouldPageChange) setChangingPage(false)
  }

  const { onInfoOpen } = useMeetModals()

  const onAccept = async ({
    public_meeting_id,
    public_meeting_participant_id,
  }) => {
    try {
      await dispatch(
        acceptMeet({ public_meeting_id, public_meeting_participant_id })
      )

      successAlert("Meet accepted")

      if (path === KNOWMAP_INCOMING_KNOWMEET) {
        history.replace(KNOWMAP_INCOMING_MEETS)
      } else {
        await fetchData()
      }
    } catch (e) {
      errorAlert(e)
    }
  }

  const onDecline = async ({ public_meeting_id }) => {
    try {
      await dispatch(declineMeet({ id: public_meeting_id }))

      successAlert("Meet declined")

      if (path === KNOWMAP_INCOMING_KNOWMEET) {
        history.replace(KNOWMAP_INCOMING_MEETS)
      } else {
        await fetchData()
      }
    } catch (e) {
      errorAlert(e)
    }
  }

  const onForward = async ({ id }) => {
    try {
      await dispatch(forwardMeet({ id }))

      successAlert("You've been moved to the next cohort")

      if (path === KNOWMAP_INCOMING_KNOWMEET) {
        history.replace(KNOWMAP_INCOMING_MEETS)
      } else {
        await fetchData()
      }
    } catch (e) {
      errorAlert(e)
    }
  }

  React.useEffect(() => {
    const { q } = qs.parse(location.search)

    if (typeof q === "string") {
      setName(q)
    }

    fetchData()
  }, [])

  const _composeQuery = () => {
    composeQuery({
      fields: {
        q: name || undefined,
      },
      history,
      path,
    })
  }

  const onSearch = (event) => {
    event.preventDefault()

    _composeQuery()
  }

  useEffect(() => {
    if (dataReceived) {
      fetchData()
      setPage(0)
    }
  }, [location.search])

  return (
    <div className="box">
      <div className="box__heading">Incoming Meets</div>

      {dataReceived && !!incomingMeets.list.length && (
        <Container>
          <div className="mb15">
            <div className="search-form search-form--compact">
              <div className="search-form__icon">
                <SearchOutlined />
              </div>
              <form onSubmit={onSearch}>
                <input
                  type="text"
                  id="search"
                  name="search"
                  placeholder="Search Meets"
                  className="search-form__field"
                  autoComplete="off"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </form>
            </div>
          </div>
          <Table
            columns={columns}
            data={incomingMeets.list}
            fluid={columns.length <= 5}
            changingPage={changingPage}
            onDetailsOpen={(record) => {
              onInfoOpen(record.raw.public_meeting_id, "confirmable")
            }}
            actions={(record) =>
              [
                record.accepted &&
                  !record.declined && {
                    component: () => (
                      <Tooltip
                        title="Move to next cohort"
                        arrow
                        key={"Forward"}
                      >
                        <UpdateIcon
                          onClick={() =>
                            onForward({
                              id: record.id,
                            })
                          }
                        />
                      </Tooltip>
                    ),
                  },
                !record.accepted &&
                  !record.declined && {
                    component: () => (
                      <Tooltip title="Accept" arrow key={"Accept Meet"}>
                        <CheckOutlined
                          onClick={() =>
                            onAccept({
                              public_meeting_id: record.public_meeting_id,
                              public_meeting_participant_id: record.id,
                            })
                          }
                        />
                      </Tooltip>
                    ),
                  },
                !record.accepted &&
                  !record.declined && {
                    component: () => (
                      <Tooltip title="Decline" arrow key={"Decline Meet"}>
                        <CloseOutlined
                          onClick={() =>
                            onDecline({
                              public_meeting_id: record.public_meeting_id,
                            })
                          }
                        />
                      </Tooltip>
                    ),
                  },
              ].filter(Boolean)
            }
          />
          <TablePagination
            count={100}
            page={page}
            onPageChange={(e, value) => setPage(value)}
            rowsPerPage={recordsPerPage}
            rowsPerPageOptions={recordsPerPageOptions}
            onRowsPerPageChange={(e) =>
              setRecordsPerPage(parseInt(e.target.value, 10))
            }
          />
        </Container>
      )}
      {dataReceived && !incomingMeets.list.length && (
        <Stub text={"You don't have incoming Meets yet"} />
      )}

      <Route
        path={KNOWMAP_INCOMING_MEET}
        render={() => (
          <MeetDialog
            open
            handleClose={(replace = false) => {
              replace
                ? history.replace(KNOWMAP_INCOMING_MEETS)
                : history.push(KNOWMAP_INCOMING_MEETS)
            }}
            isConfirmable
          />
        )}
      />
    </div>
  )
}

const IncomingMeets = () => {
  const dispatch = useDispatch()

  const getRecords = async ({ name, offset, limit } = {}) => {
    await dispatch(incomingMeetsList({ name, offset, limit }))
  }

  return (
    <MeetModalsProvider updateList={getRecords}>
      <MeetsTable getRecords={getRecords} />
    </MeetModalsProvider>
  )
}

export default IncomingMeets
